import Link from 'next/link'
import { NextSeo } from 'next-seo'
import {
  ArrowRightIcon,
  GlobeIcon,
  QuestionMarkCircleIcon,
  SearchIcon
} from '@heroicons/react/solid'

import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { PublicExplore } from '@components/PublicExplore'

export default function GetExplore() {
  return (
    <>
      <NextSeo
        title="Majordomo's Explore | Homeowners & Inspection Resources"
        description="Majordomo's Explore is a full page of resources found online – specific to your local area and property. It includes homeowner rebates, permits, maps, utilities and much more."
        canonical="https://majordomo.com/explore-homeowners-resources"
        openGraph={{
          url: 'https://majordomo.com/features/explore-homeowners-resources',
          title:
            "Majordomo's Explore | Homeowners & Inspection Resources | Majordomo",
          description:
            "Majordomo's Explore is a full page of resources found online – specific to your local area and property. It includes homeowner rebates, permits, maps, utilities and much more.",
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/explore-hero.png',
              width: 700,
              height: 681,
              alt: 'A compass, pointing North, held up in a forest'
            },
            {
              url: 'https://majordomo.com/images/product-pages/explore.png',
              width: 700,
              height: 504,
              alt: 'A compass, pointing North, held up in a forest'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex flex-col items-center justify-between pb-12 md:flex-row lg:pb-36">
          <div className="flex flex-col items-center justify-center order-last w-full md:items-start 2xl:p-36 xl:p-20 md:p-8 md:order-first">
            <div className="flex items-center pb-4 mb-4 border-b border-gray-200 md:w-full">
              <GlobeIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h1 className="text-2xl font-black tracking-wide uppercase text-brand">
                Explore
              </h1>
            </div>

            <p className="mb-8 text-xl font-light lg:mb-8 md:mb-4 lg:text-2xl md:text-lg">
              <span className="font-black">Stop</span> Searching
              <br />
              <span className="font-black">Start</span> Learning
            </p>

            <div className="w-full max-w-lg px-8 md:p-0 space-y-4">
              <p>
                Hundreds of online resources exist to help answer questions
                about home purchases, but finding useful websites that focus on
                specific properties can be needlessly time-consuming. That means
                you might be tempted to skip this critical step – or to spend
                many hours searching for that needle in a haystack. Relax:
                Majordomo has done the searching for you.
              </p>

              <p>
                Majordomo's Explore is a full page of resources found online –
                specific to your local area and property. It includes homeowner
                rebates, permits, maps, utilities and much more.
              </p>
            </div>
          </div>

          <img
            src="/images/product-pages/explore-hero.png"
            className="order-first mb-8 md:order-last md:mb-0"
            width="50%"
          />
        </div>

        <h4 className="max-w-sm px-8 pb-12 mx-auto text-lg font-black tracking-tight text-center md:text-2xl md:px-0 md:max-w-none">
          Get US, State, County and City resources for free by purchasing a{' '}
          <Link href="/features/domoreport-repair-estimates">
            <a className="text-accentBlue hover:underline">Domoreport</a>
          </Link>
        </h4>

        <div className="mb-12 lg:mb-36">
          <div className="flex flex-col px-4 lg:flex-row justify-evenly 2xl:px-16">
            <div className="flex items-center justify-center w-full lg:w-1/2 lg:p-16">
              <img
                src="/images/product-pages/explore.png"
                alt="Majordomo product samples"
                className="max-w-md xl:max-w-lg lg:max-w-sm lg:w-auto"
              />
            </div>

            <div className="flex flex-col items-center w-full p-4 space-y-12 md:p-16 lg:w-1/2">
              <div className="relative flex">
                <SearchIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white md:-left-24 md:w-16 md:h-16 bg-accentBlue"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    We've done the searching for you
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Don't waste your valuable time searching the internet. We've
                    found hundreds of online resources – and present you only
                    with the ones that are most helpful to understand your home.{' '}
                    <a
                      href="/blog/a-redesigned-explore-page-to-help-you-make-confident-home-purchasing-decisions/"
                      className="text-accentBlue hover:underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <QuestionMarkCircleIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white md:-left-24 md:w-16 md:h-16 bg-accentBlue"
                />
                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    Answer the questions you don't know you have – yet
                  </h4>
                  <p className="max-w-lg mr-auto">
                    It's part of the traditional role of a{' '}
                    <a
                      href="/blog/majordomo/"
                      className="text-accentBlue hover:underline"
                    >
                      majordomo
                    </a>
                    : Bringing potential issues to your attention before you
                    know about them. Majordomo's Explore will connect you with
                    fantastic resources you might not have found otherwise.
                  </p>
                </div>
              </div>

              <div className="relative flex">
                <GlobeIcon
                  width={56}
                  height={56}
                  className="absolute top-0 w-12 h-12 p-2 text-white md:-left-24 md:w-16 md:h-16 bg-accentBlue"
                />

                <div>
                  <h4 className="max-w-lg mt-16 mb-2 mr-auto text-lg font-black tracking-tight md:text-2xl md:mt-0">
                    National and local resources
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo's Explore includes country, state, county, and
                    city resources specific to your property.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h4 className="px-8 pb-12 text-lg font-black tracking-tight text-center md:text-2xl md:px-0">
          Sample the national resources available through Majordomo's Explore:
        </h4>

        <div className="flex items-center justify-center w-full px-8 pb-12 lg:pb-36 lg:p-0">
          <iframe
            width="840"
            height="472.4"
            src="https://www.youtube.com/embed/bRnA9xc21NA?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="hidden lg:inline"
            allowFullScreen
          />

          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/bRnA9xc21NA?hl=en_US&amp;rel=0&amp;autohide=1&amp;autoplay=0"
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media"
            title="Introducing Majordomo's Domoreport"
            className="inline lg:hidden"
            allowFullScreen
          />
        </div>

        <h4 className="px-8 pb-12 text-lg font-black tracking-tight text-center md:text-2xl md:px-0">
          Sample the national resources available through Majordomo's Explore:
        </h4>
        <PublicExplore />

        <div className="flex flex-col items-center justify-center w-full pt-8 pb-0 mb-12 md:flex-row md:space-y-0 space-y-4 md:pb-12 md:pt-0 lg:mb-36">
          <Link href="/checkout">
            <a>
              <Button variant="primary" clipSide="right">
                Order My Domoreport{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </a>
          </Link>
        </div>
      </main>

      <Footer />
    </>
  )
}
